import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, authHeaders } from '../contexts/AuthContext';
import Breadcrumbs from '../components/Breadcrumbs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  FolderKanban, Users, Bot, DollarSign,
  Clock, CheckCircle2, ArrowRight, Plus, FileText,
  ClipboardList, TrendingUp, Activity, AlertCircle, Shield
} from 'lucide-react';
import { AinaaBadge } from '../components/AinaaBadge';
import { demoProjects, recentActivity, upcomingDeadlines, demoTasks } from '../data/dummyData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

export default function DashboardPage() {
  const { user } = useAuth();
  const [dashData, setDashData] = useState(null);

  const activeProjects = useMemo(() => demoProjects.filter(p => p.status === 'active').slice(0, 4), []);

  useEffect(() => {
    fetch(`${API}/dashboard`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(setDashData)
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount

  const stats = [
    { label: 'Active Projects', value: dashData?.active_projects ?? demoProjects.filter(p => p.status === 'active').length, icon: FolderKanban },
    { label: 'Team Members', value: dashData?.team_count ?? 25, icon: Users },
    { label: 'Tasks Done', value: dashData?.tasks_done ?? demoTasks.filter(t => t.status === 'done').length, icon: CheckCircle2 },
    { label: 'Budget Used', value: dashData?.budget_used ?? '68%', icon: DollarSign },
  ];

  const quickActions = [
    { label: 'New Project', icon: Plus, path: '/projects' },
    { label: 'Find Collaborator', icon: Users, path: '/people' },
    { label: 'Ask Specter', icon: Bot, path: '/spectre' },
    { label: 'Create Call Sheet', icon: ClipboardList, path: '/spectre' },
  ];

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }} data-testid="dashboard-page">
      <Breadcrumbs />
      {/* Header */}
      <motion.div variants={fadeUp} className="mb-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight">
          Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
        </h1>
        <div className="flex items-center gap-3 mt-1">
          <p className="text-sm text-muted-foreground">Here's what's happening across your projects.</p>
          {user?.ainaa_id && <AinaaBadge ainaaId={user.ainaa_id} trustScore={user.trust_score} />}
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div variants={fadeUp} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map(s => (
          <div key={s.label} className="p-5 rounded-xl border border-border/50 bg-card" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`}>
            <div className="flex items-center justify-between mb-3">
              <s.icon className="w-4 h-4 text-muted-foreground" strokeWidth={2} />
              <TrendingUp className="w-3.5 h-3.5 text-muted-foreground/40" strokeWidth={2} />
            </div>
            <p className="font-heading text-2xl font-semibold">{s.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
          </div>
        ))}
      </motion.div>

      {/* Quick Actions */}
      <motion.div variants={fadeUp} className="flex flex-wrap gap-2 mb-8">
        {quickActions.map(a => (
          <Button key={a.label} variant="outline" size="sm" className="h-9 gap-2 text-xs" asChild data-testid={`quick-${a.label.toLowerCase().replace(/\s/g,'-')}`}>
            <Link to={a.path}>
              <a.icon className="w-3.5 h-3.5" strokeWidth={2} /> {a.label}
            </Link>
          </Button>
        ))}
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left column: Projects + Activity */}
        <div className="lg:col-span-2 space-y-6">
          {/* Active Projects */}
          <motion.div variants={fadeUp}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-medium">Active Projects</h2>
              <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" asChild>
                <Link to="/projects">View all <ArrowRight className="w-3 h-3 ml-1" /></Link>
              </Button>
            </div>
            <div className="space-y-2">
              {activeProjects.map(p => (
                <Link to={`/projects/${p.project_id}`} key={p.project_id} className="flex items-center gap-4 p-4 rounded-xl border border-border/50 bg-card hover:border-border transition-all group" data-testid={`project-${p.project_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium truncate group-hover:text-foreground">{p.name}</h3>
                      <Badge variant="secondary" className="text-[10px] h-5 px-1.5">{p.project_type}</Badge>
                    </div>
                    {p.ainaa_id && <p className="text-[10px] font-mono text-muted-foreground/70 mt-0.5">{p.ainaa_id}</p>}
                    <p className="text-xs text-muted-foreground mt-1 truncate">{p.description}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="w-24 h-1.5 rounded-full bg-secondary">
                      <div className="h-full rounded-full bg-[hsl(36,80%,52%)]" style={{ width: `${p.progress}%` }} />
                    </div>
                    <span className="text-xs text-muted-foreground w-8 text-right">{p.progress}%</span>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Recent Activity */}
          <motion.div variants={fadeUp}>
            <h2 className="font-heading text-lg font-medium mb-3">Recent Activity</h2>
            <div className="p-4 rounded-xl border border-border/50 bg-card space-y-3" data-testid="recent-activity">
              {recentActivity.slice(0, 5).map(a => (
                <div key={a.id} className="flex items-start gap-3">
                  <Activity className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" strokeWidth={2} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs"><span className="font-medium">{a.action}</span> <span className="text-muted-foreground">in {a.project}</span></p>
                    <p className="text-[10px] text-muted-foreground">{a.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Right column: Deadlines + AI Insights */}
        <div className="space-y-6">
          {/* Upcoming Deadlines */}
          <motion.div variants={fadeUp}>
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-muted-foreground" strokeWidth={2} />
              <h2 className="font-heading text-lg font-medium">Deadlines</h2>
            </div>
            <div className="space-y-2" data-testid="upcoming-deadlines">
              {upcomingDeadlines.map(d => (
                <div key={d.id} className="flex items-center justify-between p-3.5 rounded-xl border border-border/50 bg-card">
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate">{d.task}</p>
                    <p className="text-[10px] text-muted-foreground">{d.project}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] text-muted-foreground">{d.date}</span>
                    {d.priority === 'high' && <AlertCircle className="w-3 h-3 text-foreground" strokeWidth={2} />}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Specter Insights */}
          <motion.div variants={fadeUp}>
            <div className="flex items-center gap-2 mb-3">
              <Bot className="w-4 h-4 text-foreground" strokeWidth={2} />
              <h2 className="font-heading text-lg font-medium">Specter Insights</h2>
            </div>
            <div className="space-y-2 p-4 rounded-xl border border-border/50 bg-card" data-testid="ai-insights-card">
              {(dashData?.ai_suggestions || [
                "Echoes of Mumbai is 15% behind schedule. Consider adding a second unit.",
                "Payment to Arjun Mehta is 5 days overdue. Prioritize to maintain trust score.",
                "3 new collaborators matching your sound design search are available.",
              ]).map((s) => (
                <div key={s} className="flex items-start gap-2">
                  <Bot className="w-3 h-3 text-muted-foreground mt-0.5 flex-shrink-0" strokeWidth={2} />
                  <p className="text-xs text-muted-foreground leading-relaxed">{s}</p>
                </div>
              ))}
              <Button variant="ghost" size="sm" className="w-full mt-2 text-xs gap-1.5" asChild data-testid="ask-spectre-btn">
                <Link to="/spectre"><Bot className="w-3 h-3" strokeWidth={2} /> Ask Specter</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
